import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Ref } from 'vue';
import type { IMeResponse } from '@/types/apiTypes';
import type { Maybe } from '@nova/core';
import AxiosPlugin from '@satellite/plugins/AxiosPlugin';
import { queryClient } from '@/plugins/vueQuery';
import authService from '@satellite/services/auth-service';

export const JWT_TOKEN_KEY = 'access_token';

export const useAuthStore = defineStore('auth', () => {
  const user: Ref<IMeResponse | null> = ref(null);

  async function logout() {
    setUser(null);
    localStorage.removeItem(JWT_TOKEN_KEY);
    AxiosPlugin.setAccessToken(null);
    await queryClient.cancelQueries();
    queryClient.clear();
  }

  function setUser(newUser: IMeResponse | null) {
    user.value = newUser;
  }

  function setToken(token: string): void {
    localStorage.setItem(JWT_TOKEN_KEY, token);
    AxiosPlugin.setAccessToken(token);
  }

  function clearToken(): void {
    localStorage.removeItem(JWT_TOKEN_KEY);
    AxiosPlugin.setAccessToken(null);
  }

  async function loginWithToken(token: string): Promise<void> {
    setToken(token);

    try {
      const userData = (await authService.getMe()) as Maybe<IMeResponse>;

      if (userData) {
        setUser(userData);
      } else {
        clearToken();
        throw new Error('Failed to fetch user data');
      }
    } catch (error) {
      clearToken();
      throw error;
    }
  }

  return { user, logout, setUser, setToken, loginWithToken };
});
